// Global Instructions Rule Applied!
//
// Renders a cfg-authored guide inside the help drawer.
//
// Each section is a standard FormSection — the canonical section wrapper
// (docs/FRONTEND_DESIGN_PATTERNS.md) — so the guide is built from the same kit
// as every other screen. Prose goes through ShadowRichHtml, which sanitizes it
// with DOMPurify and isolates its styles; a section carrying a component_key
// additionally renders the registered component beneath its prose.

import React from 'react'
import { Alert, Empty, Spin } from 'antd'
import { FormSection } from '../FormSection.tsx'
import { ShadowRichHtml } from '../ShadowRichHtml.tsx'
import { HELP_ARTICLE_CSS } from './helpArticleCss.ts'
import { getHelpSectionComponent } from '../../../ui_configs/helpSections.ts'
import type { HelpArticle } from '../../../state_manager/data_models/help_article_model.ts'

export interface HelpArticleBodyProps {
  article: HelpArticle | null
  loading: boolean
  error?: string | null
}

export function HelpArticleBody({ article, loading, error }: HelpArticleBodyProps) {
  if (loading) {
    return (
      <div className='flex h-full items-center justify-center'>
        <Spin />
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        showIcon
        type='warning'
        message='This guide could not be loaded'
        description='Close the guide and open it again. Everything on the page behind it still works.'
      />
    )
  }

  // A screen may point at a guide nobody has written yet. That is a normal
  // state, not a failure.
  if (!article || article.sections.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description='No guide has been written for this screen yet.'
      />
    )
  }

  return (
    <>
      {article.sections.map((section, index) => {
        const SectionComponent = getHelpSectionComponent(section.component_key)
        return (
          <FormSection
            key={`${section.heading}-${index}`}
            title={section.heading}
            titleColor='blue'
            scrollable={false}
            className='mb-2'
          >
            {section.body_html ? (
              <ShadowRichHtml html={section.body_html} css={HELP_ARTICLE_CSS} />
            ) : null}
            {SectionComponent ? <SectionComponent /> : null}
          </FormSection>
        )
      })}
    </>
  )
}
